/**
 * A key, drawn the way the boards draw one: 20px tall, a hairline, white, the
 * letter at 11/600 in the muted ink (`48-new-request`, the nav's **N** and the
 * menus' **N R**). One component, because a shortcut said in three places is
 * three keycaps that drift apart.
 */
import { cn } from '../lib/cn.js'

export function Keycap({ children, className }: { children: string; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 items-center rounded-[5px] border border-line bg-surface px-1.5',
        'font-sans text-[11px] font-semibold text-text-muted',
        className,
      )}
    >
      {children}
    </kbd>
  )
}

/** Several keys pressed one after another — *N R*. */
export function Keys({ keys, className }: { keys: readonly string[]; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-[3px]', className)} aria-label={keys.join(' then ')}>
      {keys.map((key) => <Keycap key={key}>{key}</Keycap>)}
    </span>
  )
}

/**
 * The + the request rows and the nav's tile carry: a heavier stroke than the
 * icon set's 1.7, because at 14px on a solid tile the lighter one reads as a
 * smudge (`new-request.md` §13.2: "a white + (14 px, stroke 2.8)").
 */
export function PlusGlyph({ size = 14, stroke = 2.8, className }: {
  size?: number; stroke?: number; className?: string
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden
      className={cn('shrink-0', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

/**
 * The ✓ the boards draw on *Create request* and in a section's ready circle
 * (`M5 12.5l4.5 4.5L19 7.5`), heavier where it sits on a small solid circle.
 */
export function CheckGlyph({ size = 15, stroke = 2, className }: {
  size?: number; stroke?: number; className?: string
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden
      className={cn('shrink-0', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  )
}
